
import { ReactNode } from "react";
import { PATH_CONSTANTS, ADMIN_PATH_SEGMENTS, BASE_PATHS } from "./pathConstants";

// Define the protection types for routes
export type RouteProtectionType = 'auth' | 'none';

// Re-export path constants
export const PATH = PATH_CONSTANTS;

// Define the route configuration interface
export interface RouteConfig {
  path: string;
  component: React.ComponentType<any>; 
  protection: RouteProtectionType;
  children?: RouteConfig[];
  meta?: {
    requiresIntegration?: string;
    requiredPermissions?: string[];
    isPrivate?: boolean; // If true, adds noindex meta tag and excludes from sitemap
  };
}

// Import all page components
import Index from "../pages/Index";
import Landing from "../pages/Landing";
import AboutUs from "../pages/AboutUs";
import TermsOfService from "../pages/TermsOfService";
import PrivacyPolicy from "../pages/PrivacyPolicy";
import Services from "../pages/Services";
import ServiceView from "../pages/ServiceView";
import Auth from "../pages/Auth";
import RequestResetPassword from "../pages/RequestResetPassword";
import ResetPassword from "../pages/ResetPassword";
import Profile from "../pages/Profile";
import Conversations from "../pages/Conversations";
import ServiceRequest from "../pages/ServiceRequest";
import ReviewPotentialMatch from "../pages/ReviewPotentialMatch";
import RegisterBusiness from "../pages/RegisterBusiness";
import FinalizeRegistration from "../pages/FinalizeRegistration";
import NotFound from "../pages/NotFound";
import AdminDashboard from "../pages/admin/Dashboard";
import CommunityManagement from "../pages/admin/CommunityManagement";
import CategoryManagement from "../pages/admin/CategoryManagement";
import BadgeManagement from "../pages/admin/BadgeManagement";
import UserManagement from "../pages/admin/UserManagement";
import BusinessManagement from "../pages/admin/BusinessManagement";
import ReviewManagement from "../pages/admin/ReviewManagement";
import DiscountManagement from "../pages/admin/DiscountManagement";
import TranslationsManagement from "../pages/admin/TranslationsManagement";
import EmailTemplateManagement from "../pages/admin/EmailTemplateManagement";
import CommunicationManagement from "../pages/admin/CommunicationManagement";
import CampaignManagement from "../pages/admin/CampaignManagement";
import ContentManagement from "../pages/admin/ContentManagement";
import SubscriptionFeaturesManagement from "../pages/admin/SubscriptionFeaturesManagement";
import MyBusiness from "../pages/MyBusiness";
import AdminLayout from "../components/admin/AdminLayout";
import SubscriptionPage from "../pages/business/subscription";
import SubscriptionSuccessPage from "../pages/business/subscription/success";
import SubscriptionCancelPage from "../pages/business/subscription/cancel";
import IntegrationsPage from "../components/admin/IntegrationsPage";
import MyServiceRequests from "../pages/MyServiceRequests";
import ServiceRequestDetail from "../pages/ServiceRequestDetail";
import MyJobOpportunities from "../pages/MyJobOpportunities";
import JobProposalDetail from "../pages/JobProposalDetail";
import BusinessChat from "../pages/admin/BusinessChat";

// Define the routes configuration using the PATH constants
export const routes: RouteConfig[] = [
  { path: PATH.HOME, component: Index, protection: 'none' },
  { path: PATH.LANDING, component: Landing, protection: 'none' },  
  { path: PATH.ABOUT, component: AboutUs, protection: 'none' },
  { path: PATH.TERMS, component: TermsOfService, protection: 'none' },
  { path: PATH.PRIVACY, component: PrivacyPolicy, protection: 'none' },
  { path: PATH.SERVICES, component: Services, protection: 'none' },
  { path: PATH.SERVICE_VIEW, component: ServiceView, protection: 'none' },
  { path: PATH.AUTH, component: Auth, protection: 'none' },
  { path: PATH.AUTH_REQUEST_RESET, component: RequestResetPassword, protection: 'none' },
  { path: PATH.AUTH_RESET, component: ResetPassword, protection: 'none' },
  { path: PATH.PROFILE, component: Profile, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.CONVERSATIONS, component: Conversations, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.CONVERSATIONS_ID, component: Conversations, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.SERVICE_REQUEST, component: ServiceRequest, protection: 'auth' },
  { path: PATH.SERVICE_REQUEST_EDIT, component: ServiceRequest, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.SERVICE_REQUEST_REVIEW, component: ReviewPotentialMatch, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.REVIEW_POTENTIAL_MATCH, component: ReviewPotentialMatch, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.MY_SERVICE_REQUESTS, component: MyServiceRequests, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.MY_SERVICE_REQUESTS_ID, component: ServiceRequestDetail, protection: 'auth', meta: { isPrivate: true } },
  { path: PATH.REGISTER_BUSINESS, component: RegisterBusiness, protection: 'auth' },
  { path: PATH.COMPLETE_REGISTRATION, component: FinalizeRegistration, protection: 'none', meta: { isPrivate: true } },
  {
    path: BASE_PATHS.ADMIN,
    component: AdminLayout,
    protection: 'auth',
    meta: { isPrivate: true },
    children: [
      { path: ADMIN_PATH_SEGMENTS.ROOT, component: AdminDashboard, protection: 'auth', meta: { isPrivate: true } },
      { 
        path: ADMIN_PATH_SEGMENTS.SUBSCRIPTION, 
        component: SubscriptionPage, 
        protection: 'auth',
        meta: { requiresIntegration: 'stripe', isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.SUBSCRIPTION_SUCCESS, 
        component: SubscriptionSuccessPage, 
        protection: 'auth',
        meta: { requiresIntegration: 'stripe', isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.SUBSCRIPTION_CANCEL, 
        component: SubscriptionCancelPage, 
        protection: 'auth',
        meta: { requiresIntegration: 'stripe', isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.COMMUNITY, 
        component: CommunityManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['edit_communities'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.CATEGORIES, 
        component: CategoryManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_categories'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.BADGES, 
        component: BadgeManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_badges'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.USERS, 
        component: UserManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_users'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.BUSINESSES, 
        component: BusinessManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['validate_businesses'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.REVIEWS, 
        component: ReviewManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['validate_reviews'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.DISCOUNTS, 
        component: DiscountManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['validate_businesses'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.TRANSLATIONS, 
        component: TranslationsManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_translations'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.EMAIL_TEMPLATES, 
        component: EmailTemplateManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_email_templates'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.COMMUNICATIONS, 
        component: CommunicationManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_communications'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.CAMPAIGNS, 
        component: CampaignManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_communications'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.CONTENT_MANAGEMENT, 
        component: ContentManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_cms'], isPrivate: true }
      },
      { 
        path: ADMIN_PATH_SEGMENTS.SUBSCRIPTION_FEATURES, 
        component: SubscriptionFeaturesManagement, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_features'], isPrivate: true }
      },
      { path: ADMIN_PATH_SEGMENTS.MY_BUSINESS, component: MyBusiness, protection: 'auth', meta: { isPrivate: true } },
      { path: ADMIN_PATH_SEGMENTS.MY_JOB_OPPORTUNITIES, component: MyJobOpportunities, protection: 'auth', meta: { isPrivate: true } },
      { path: ADMIN_PATH_SEGMENTS.JOB_PROPOSAL_DETAIL, component: JobProposalDetail, protection: 'auth', meta: { isPrivate: true } },
      { path: ADMIN_PATH_SEGMENTS.BUSINESS_CHAT, component: BusinessChat, protection: 'auth', meta: { isPrivate: true } },
      { 
        path: ADMIN_PATH_SEGMENTS.INTEGRATIONS, 
        component: IntegrationsPage, 
        protection: 'auth',
        meta: { requiredPermissions: ['manage_integrations'], isPrivate: true }
      }
    ]
  },
  { path: "*", component: NotFound, protection: 'none' }
];
